import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import "./MatchPairsAnswers.scss"
import Button from "@ui/Button"
import i18nInstance from "@helper/i18nInstance"
import { useQuiz } from "@Quiz/context"
import _ from "lodash"
import ContainedImg from "@Quiz/Ui/ContainedImg"
import useOnSwipe from "@helper/useOnSwipe"
import onEnterOrSpace from "@helper/onEnterOrSpace"

interface MatchPairsAnswersProps {
  conf: Quiz.Api.MatchPairs.QuestionConfig
  tryAgain?: Quiz.Api.MatchPairs.TryAgainInfo
}

interface MatchPairsState {
  selectedMatches: Quiz.Api.MatchPairs.MatchForSubmission[]
  markEvalErrors: boolean
  canSubmitInfo: CanSubmitInfo
}

type CanSubmitInfo =
  | { can: true }
  | { can: false, reason: "notAllMatched" | "duplicateMatching"}

const calcCanSubmitInfo = (config: Quiz.Api.MatchPairs.QuestionConfig, matches: Quiz.Api.MatchPairs.MatchForSubmission[]): CanSubmitInfo => {
  if (matches.length < config.options_left.length) {
    return { can: false, reason: "notAllMatched" }
  } else {
    const rightVals = _.map(matches, "right")
    const hasDuplicate = _.uniq(rightVals).length !== rightVals.length
    if (hasDuplicate) {
      return { can: false, reason: "duplicateMatching" }
    }
  }
  return { can: true }
}

const useMatchPairsState = (config: Quiz.Api.MatchPairs.QuestionConfig, tryAgain?: Quiz.Api.MatchPairs.TryAgainInfo) => {
  const [ state, setState ] = useState<MatchPairsState>({
    selectedMatches: [],
    markEvalErrors: false,
    canSubmitInfo: { can: false, reason: "notAllMatched" }
  })
  const configRef = useRef(config)
  configRef.current = config
  const selectedMatchesRef = useRef(state.selectedMatches)
  selectedMatchesRef.current = state.selectedMatches
  const { busy, quiz } = useQuiz()

  const onMakeMatch = useCallback((match: Quiz.Api.MatchPairs.MatchForSubmission) => { // toggle val-selection, clear markEvalErrors
    setState((prev) => {
      const newMatches: MatchPairsState["selectedMatches"] = [...prev.selectedMatches.filter((m) => m.left !== match.left), match]
      return {
        selectedMatches: newMatches,
        markEvalErrors: false, // always clear on selection change
        canSubmitInfo: calcCanSubmitInfo(configRef.current, newMatches),
      }
    })
  }, [])

  const onRemoveMatch = useCallback((left: number) => {
    setState((prev) => {
      const newMatches = prev.selectedMatches.filter((m) => m.left !== left)
      return {
        selectedMatches: newMatches,
        markEvalErrors: false, // always clear on selection change
        canSubmitInfo: calcCanSubmitInfo(configRef.current, newMatches),
      }
    })
  }, [])

  // Cycles the match for options_left val `left` through options_right in order: no
  // match -> first -> second -> ... -> last. Doesn't overflow from last back to no
  // match on "next" - only cycling "prev" from the first entry removes the match.
  const onCycleMatch = useCallback((left: number, direction: "prev" | "next") => {
    const rightVals = _.map(configRef.current.options_right, "val")
    const currentMatch = _.find(selectedMatchesRef.current, (m) => m.left === left)
    const currentIndex = currentMatch ? rightVals.indexOf(currentMatch.right) : -1

    const newIndex = direction === "next"
      ? Math.min(currentIndex + 1, rightVals.length - 1)
      : Math.max(currentIndex - 1, -1)

    if (newIndex === -1) {
      onRemoveMatch(left)
    } else {
      onMakeMatch({ left, right: rightVals[newIndex] })
    }
  }, [onMakeMatch, onRemoveMatch])

  const hasEval = !!quiz?.question?.eval

  useEffect(() => { // set markEvalErrors when eval but not (no longer) busy
    if (!busy && hasEval && tryAgain) {
      setState({
        selectedMatches: tryAgain.previous_matches,
        markEvalErrors: true,
        canSubmitInfo: calcCanSubmitInfo(configRef.current, tryAgain.previous_matches),
      })
    }
  }, [tryAgain, hasEval, busy])

  return { state, onMakeMatch, onRemoveMatch, onCycleMatch }
}

export const MatchPairsResultInfo: React.FC<{ question: Quiz.Api.MatchPairsQuestion}> = () => {
  return null
}

const getSelectedRightEntry = (
  config: Quiz.Api.MatchPairs.QuestionConfig,
  selectedMatches: Quiz.Api.MatchPairs.MatchForSubmission[],
  leftVal: number
): Quiz.Api.AnswerOptionBase | undefined => {
  const match = _.find(selectedMatches, (m) => m.left === leftVal)
  if (!match) { return undefined }
  return _.find(config.options_right, (opt) => opt.val === match.right)
}

type LeftEntryWithPickerProps =
  {
    me: Quiz.Api.MatchPairs.QuestionConfig["options_left"][number]
    conf: Quiz.Api.MatchPairs.QuestionConfig
    currentMatch?: Quiz.Api.MatchPairs.QuestionConfig["options_right"][number]
    whoIsMatchedMultipleTimes: number[]
    canCyclePrev: boolean
    canCycleNext: boolean
  } &
  Pick<ReturnType<typeof useMatchPairsState>, "onCycleMatch" | "onMakeMatch" | "onRemoveMatch">

const LeftLargeElement: React.FC<LeftEntryWithPickerProps> = ({ me}) => {
  const imageAndTextHolderClassName = [
    "neo__matchPairs__leftLarge__imageAndTextHolder",
    me.text && !me.image ? "neo__matchPairs__leftLarge__imageAndTextHolder--onlyText" : "",
    me.image && !me.text ? "neo__matchPairs__leftLarge__imageAndTextHolder--onlyImage" : "",
  ].join(" ")

  return <div>
    <div className={imageAndTextHolderClassName}>
      {me.image &&
        <span className="neo__matchPairs__leftLarge__image">
          <ContainedImg className="neo__matchPairs__leftLarge__image__img" src={me.image["1x"]} displaySize={me.image_display_size} />
          {me.image_credit && <div className="neo__matchPairs__leftLarge__image__imgCredit">{me.image_credit}</div>}
        </span>}
      {me.text &&
        <span className="neo__matchPairs__leftLarge__text" dangerouslySetInnerHTML={{__html: me.text}} />}
    </div>  
  </div>
}

type RightSmallerElementProps =
  Pick<LeftEntryWithPickerProps, "conf" | "onCycleMatch" | "onMakeMatch" | "onRemoveMatch" | "canCyclePrev" | "canCycleNext"> &
  {
    left: Quiz.Api.MatchPairs.QuestionConfig["options_left"][number]
    me: Quiz.Api.MatchPairs.QuestionConfig["options_right"][number]
    markDuplicateMatch: boolean
    isPickedAsMatch: boolean
  }

const RightSmallerElement: React.FC<RightSmallerElementProps> = ({ me, markDuplicateMatch, isPickedAsMatch }) => {
  const imageAndTextHolderClassName = [
    "neo__matchPairs__rightSmaller__imageAndTextHolder",
    me.text && !me.image ? "neo__matchPairs__rightSmaller__imageAndTextHolder--onlyText" : "",
    me.image && !me.text ? "neo__matchPairs__rightSmaller__imageAndTextHolder--onlyImage" : "",
  ].join(" ")

  // Only the currently picked option is actually visible (the others sit off-screen in
  // the swiper via transform) - hide the rest from the accessibility tree too, so a
  // screen reader doesn't read out every option at once.
  return <div className={imageAndTextHolderClassName} aria-hidden={!isPickedAsMatch}>
    {markDuplicateMatch && <span className="neo__matchPairs__rightSmaller__markDuplicateMatch">{i18nInstance.t("quiz.question.match_pairs.mark_duplicate_match")}</span>}
    {me.image &&
      <span className="neo__matchPairs__rightSmaller__image">
        <ContainedImg className="neo__matchPairs__rightSmaller__image__img" src={me.image["1x"]} displaySize={me.image_display_size} />
        {me.image_credit && <div className="neo__matchPairs__rightSmaller__image__imgCredit">{me.image_credit}</div>}
      </span>}
    {me.text &&
      <span className="neo__matchPairs__rightSmaller__text" dangerouslySetInnerHTML={{__html: me.text}} />}
  </div>
}

const LeftEntryWithPicker: React.FC<LeftEntryWithPickerProps> = (props) => {
  const selectedIndex = props.currentMatch
    ? props.conf.options_right.findIndex((r) => r.val === props.currentMatch?.val) + 1
    : 0

  // swipe right = reveal the previous option (cycle "prev"), swipe left = reveal the
  // next one (cycle "next") - matches the "<"/">" links doing the same thing
  const { onCycleMatch, me, currentMatch } = props
  const onSwipe = useCallback((direction: "left" | "right") => {
    onCycleMatch(me.val, direction === "right" ? "prev" : "next")
  }, [onCycleMatch, me.val])
  const optionsHolderRef = useOnSwipe<HTMLDivElement>(onSwipe)

  const leftLabel = me.text ?? String(me.val)
  const currentMatchLabel = currentMatch && (currentMatch.text ?? String(currentMatch.val))
  const isDuplicate = !!currentMatch && props.whoIsMatchedMultipleTimes.indexOf(currentMatch.val) > -1

  return <div className="neo__matchPairs__entry">
    <div className="neo__matchPairs__entry__leftElHolder">
      <LeftLargeElement {...props} />
    </div>
    <div
      className="neo__matchPairs__entry__rightElementsSwitcher"
      role="group"
      aria-label={i18nInstance.t("quiz.question.match_pairs.aria_group_label", { left: leftLabel })}
      aria-invalid={isDuplicate}>
      <a className={`neo__matchPairs__entry__rightElementsSwitcher__prev neo__matchPairs__entry__rightElementsSwitcher__prev--${props.canCyclePrev ? "enabled" : "disabled"}`}
        role="button"
        tabIndex={0}
        aria-disabled={!props.canCyclePrev}
        aria-label={i18nInstance.t("quiz.question.match_pairs.aria_prev_option")}
        onClick={() => props.onCycleMatch(props.me.val, "prev")}
        onKeyDown={onEnterOrSpace(() => props.onCycleMatch(props.me.val, "prev"))}
        >{"<"}</a>
      <div className="neo__matchPairs__entry__rightElementsSwitcher__optionsHolder" ref={optionsHolderRef} data-testid="quiz-match-pairs-swiper">
        <div className="neo__matchPairs__entry__rightElementsSwitcher__optionsHolder__inner" style={{ transform: `translateX(-${selectedIndex * 100}%)`}}>
          <div className="neo__matchPairs__entry__rightElementsSwitcher__pleaseChooseElement" aria-hidden={!!currentMatch}>
            {i18nInstance.t("quiz.question.match_pairs.please_pick_a_match")}
          </div>
          {props.conf.options_right.map((r) => {
            return <RightSmallerElement
              key={r.val}
              conf={props.conf}
              left={props.me}
              me={r}
              isPickedAsMatch={props.currentMatch?.val === r.val}
              onCycleMatch={props.onCycleMatch}
              onMakeMatch={props.onMakeMatch}
              onRemoveMatch={props.onRemoveMatch}
              canCyclePrev={props.canCyclePrev}
              canCycleNext={props.canCycleNext}
              markDuplicateMatch={props.currentMatch?.val === r.val && props.whoIsMatchedMultipleTimes.indexOf(r.val) > -1}
            />
          })}
        </div>
      </div>
      <a className={`neo__matchPairs__entry__rightElementsSwitcher__next neo__matchPairs__entry__rightElementsSwitcher__next--${props.canCycleNext ? "enabled" : "disabled"}`}
        role="button"
        tabIndex={0}
        aria-disabled={!props.canCycleNext}
        aria-label={i18nInstance.t("quiz.question.match_pairs.aria_next_option")}
        onClick={() => props.onCycleMatch(props.me.val, "next")}
        onKeyDown={onEnterOrSpace(() => props.onCycleMatch(props.me.val, "next"))}
        style={{opacity: props.canCycleNext ? 1 : .1}}>{">"}</a>
      {/* announces the current selection (and duplicate status) to screen readers without needing focus to move */}
      <span className="neo__matchPairs__srOnly" aria-live="polite" data-testid="quiz-match-pairs-current-selection">
        {currentMatchLabel ?? i18nInstance.t("quiz.question.match_pairs.aria_no_match_selected")}
        {isDuplicate && ` - ${i18nInstance.t("quiz.question.match_pairs.mark_duplicate_match")}`}
      </span>
    </div>
  </div>
}

const MatchPairsAnswers: React.FC<MatchPairsAnswersProps> = ({ conf, tryAgain }) => {
  const { state, onMakeMatch, onCycleMatch, onRemoveMatch } = useMatchPairsState(conf, tryAgain)
  const { actions } = useQuiz()
  const selectedMatchesRef = useRef(state.selectedMatches)
  selectedMatchesRef.current = state.selectedMatches

  const whoIsMatchedMultipleTimes = useMemo(() => {
    const countByRightVal = _.countBy(state.selectedMatches, "right")
    return _.chain(countByRightVal).pickBy((count) => count > 1).keys().map(Number).value()
  }, [state.selectedMatches])
  
  const answerQuestion = useCallback(() => {
    actions.answerQuestion({ type: "match_pairs", matches: selectedMatchesRef.current })
  }, [actions])

  return <div className="neo__matchPairs__holder">
    <div className="neo__matchPairs__answerInfo">{i18nInstance.t("quiz.question.match_pairs.title_what_todo")}</div>
    <div className="neo__matchPairs__answers">
      {conf.options_left.map((l) => {
        const currentMatch = getSelectedRightEntry(conf, state.selectedMatches, l.val)
        const currentMatchIndex = currentMatch ? _.findIndex(conf.options_right, (opt) => opt.val === currentMatch.val) : -1
        return <LeftEntryWithPicker
          key={l.val}
          conf={conf}
          currentMatch={currentMatch}
          whoIsMatchedMultipleTimes={whoIsMatchedMultipleTimes}
          canCyclePrev={!!currentMatch}
          canCycleNext={conf.options_right.length > 0 && currentMatchIndex < conf.options_right.length - 1}
          me={l}
          onCycleMatch={onCycleMatch}
          onMakeMatch={onMakeMatch}
          onRemoveMatch={onRemoveMatch}
        />
      })}
    </div>
    <div className="neo__matchPairs__submitButtonHolder">
      {!state.canSubmitInfo.can &&
        <span className="neo__matchPairs__cantSubmitReason">
          {i18nInstance.t(`quiz.question.match_pairs.cant_submit_because_${state.canSubmitInfo.reason}`)}
        </span>}
      <Button        
        onClick={state.canSubmitInfo.can ? answerQuestion : undefined}>
        {i18nInstance.t("quiz.question.match_pairs.btn_send_answer")}
      </Button>
    </div>
  </div>
}

export default MatchPairsAnswers
